import type { JobUpdateOutcome } from '../types/job-update-outcome.type';

export interface JobUpdateSnackbar {
  severity: 'success' | 'critical';
  message: string;
  /**
   * False on a partial failure: the failed save's fallback payload lives only in this
   * mounted form's local state, so exiting now strands the user with no way to retry
   * without re-entering the lost values (the next load re-fetches the still-failed
   * server state, not what they just typed).
   */
  shouldExit: boolean;
}

/**
 * A failed custom-skill/machine save must not be masked by the "Job updated successfully"
 * snackbar that follows it -- SnackbarService only ever shows one snackbar at a time.
 */
export function determineJobUpdateSnackbar(outcome: JobUpdateOutcome): JobUpdateSnackbar {
  const failedParts = [
    outcome.customSkillSaveFailed ? 'custom skills' : null,
    outcome.customMachineSaveFailed ? 'custom machines' : null,
  ].filter((part): part is string => part !== null);

  if (failedParts.length === 0) {
    return { severity: 'success', message: 'Job updated successfully', shouldExit: true };
  }

  return {
    severity: 'critical',
    message: `Job updated, but could not save ${failedParts.join(' and ')}. Please try again.`,
    shouldExit: false,
  };
}
